<template>
  <div class="product-template">
    <form @submit="submitForm($event)">
      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <label for="product"
              >Item Name
              <span
                class="invalid"
                v-if="veeErrors.first('product') && !data.product.label"
              >
                - Field is required
              </span>
            </label>
            <input
              name="product"
              type="text"
              v-model.trim="data.product.label"
              placeholder="Enter item name"
              class="form-control"
              v-validate="'required|max:100'"
              data-vv-as="item name"
            />
            <div
              v-if="veeErrors.first('product') && data.product.label"
              class="invalid-feedback"
            >
              {{ veeErrors.first("product") }}
            </div>
          </div>
        </div>
        <div class="col-12">
          <div
            :class="[
              'form-group position-relative',
              { 'disabled--field': isLinked }
            ]"
          >
            <label for="location"
              >Location
              <span class="invalid" v-if="data.location == ''">
                - Field is required
              </span>
            </label>
            <Spinner class="ml-2" v-if="dataLoaders.locations" />
            <CustomSelect
              :disabled="isLinked"
              :options="locations"
              :placeholder="'Choose a location'"
              v-model="data.location"
              @handleOptions="
                params => {
                  fetchLocations(
                    this.isSearching.locations ? params : { search: '' }
                  );
                }
              "
              id="location"
              name="location"
              @typing="handleType('locations')"
              :searchable="true"
              :isLoading="dataLoaders.locations"
              form_type="form_type"
            />
            <div class="tooltipText">
              <p>To change location, please remove product link</p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label for="category"
              >Category
              <span class="invalid" v-if="data.category == ''">
                - Field is required
              </span>
            </label>
            <Spinner class="ml-2" v-if="dataLoaders.categories" />
            <CustomSelect
              :options="categories"
              :placeholder="'Choose a category'"
              v-model="data.category"
              @handleOptions="
                params => {
                  fetchCategories(
                    this.isSearching.categories ? params : { search: '' }
                  );
                }
              "
              id="category"
              name="category"
              @typing="handleType('categories')"
              :searchable="true"
              :isLoading="dataLoaders.categories"
              :config="{
                displaySize: 4
              }"
              form_type="form_type"
            />
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label for="schedule"
              >Schedule
              <span class="invalid" v-if="data.schedule == ''">
                - Field is required
              </span>
            </label>
            <Spinner class="ml-2" v-if="dataLoaders.schedules" />
            <CustomSelect
              :options="schedules"
              :placeholder="'Choose a schedule'"
              v-model="data.schedule"
              @handleOptions="
                params => {
                  fetchSchedules(
                    this.isSearching.schedules ? params : { search: '' }
                  );
                }
              "
              id="schedule"
              name="schedule"
              class="schedule-dropdown"
              @typing="handleType('schedules')"
              :searchable="true"
              :isLoading="dataLoaders.schedules"
              form_type="form_type"
            />
          </div>
        </div>
        <div class="col-12 mb-2">
          <div
            class="form-group m-0 position-relative"
            v-on-clickaway="handleHideDropdown"
          >
            <ActionButton
              :theme="{ primary, secondary, event, danger }"
              label="Configure Terms"
              style="width: auto; margin-top: 0; font-size: 0.75rem;"
              @customEvent="showDropdown = !showDropdown"
            />
            <div :class="`term-dropdown d-flex ${handleListClass}`">
              <div class="col-md-6" :key="term.value" v-for="term in terms">
                <Checkbox
                  :value="data.terms.includes(term.value)"
                  :label="term.label"
                  @toggled="handleTermToggle(term)"
                />
              </div>
              <div class="col-12">
                <hr class="m-0 mb-3" />
                <ActionButton
                  :theme="{ primary, secondary, event, danger }"
                  label="Apply"
                  style="width: auto; margin-top: 0; font-size: 0.75rem;"
                  @customEvent="handleApplyTerm()"
                />
              </div>
            </div>
          </div>
        </div>
        <template v-if="termTable.length">
          <div class="col-6">
            <div class="form-group m-0">
              <label>Term</label>
            </div>
          </div>
          <div class="col-6">
            <div class="form-group m-0">
              <label>
                Price
                <span class="invalid" v-if="!proceed">
                  - Field is required
                </span>
              </label>
            </div>
          </div>
          <div class="col-12 product-term-holder">
            <div class="d-flex uni-row" v-for="(term, i) in termTable" :key="i">
              <div class="col-6">
                <div class="form-group">
                  <input
                    name="termId[]"
                    type="text"
                    readonly
                    :placeholder="term.label"
                    class="form-control"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group d-flex align-items-center">
                  <input
                    name="price[]"
                    type="text"
                    v-model.number="term.price"
                    placeholder="1"
                    class="form-control mr-4"
                    :class="term.price ? '' : 'custom-is-invalid'"
                    @keypress="isNumber($event)"
                  />
                  <action-button
                    label=""
                    class="btn-danger"
                    style="margin-right: 0; margin-top: 0;"
                    @customEvent="handleDeleteTerm(term)"
                  >
                    <FetchingOverlay
                      v-if="loaders.deleteButton === term.value"
                    />
                    <span>
                      &mdash;
                    </span>
                  </action-button>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div class="col-12">
          <div class="form-group mt-3 d-flex">
            <label for="inventory-count">Inventory Count</label>
          </div>
        </div>
        <div class="col-12">
          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <div
                  class="input-group quantity--wrapper"
                  :class="{ disabled: !quantityStatus }"
                >
                  <div class="input-group-prepend quantity--btn">
                    <ActionButton
                      mode="outline"
                      style="margin-top:0; width: 40px; border: none;"
                      :color="'#232323'"
                      :theme="{ primary, secondary, event, danger }"
                      label="&mdash;"
                      :disabled="!quantityStatus || data.quantity <= 1"
                      @customEvent="handleItemCount('quantity', 'sub')"
                    />
                  </div>
                  <input
                    type="text"
                    class="form-control text-center quantity--field"
                    placeholder="1"
                    :value="quantityStatus ? data.quantity : 1"
                    :disabled="!quantityStatus"
                    @input="e => (data.quantity = e.target.value)"
                    @keypress="isNumber($event)"
                  />
                  <div class="input-group-append quantity--btn">
                    <ActionButton
                      mode="outline"
                      style="margin-top:0; width: 40px; border: none;"
                      :color="'#232323'"
                      :theme="{ primary, secondary, event, danger }"
                      label="+"
                      :disabled="!quantityStatus"
                      @customEvent="handleItemCount('quantity', 'add')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <div class="css-control css-control-inline css-checkbox">
              <input
                type="checkbox"
                class="css-control-input"
                id="qty-toggle"
                @click="handleItemStatus('quantity')"
                :checked="!quantityStatus"
              />
              <label class="css-control-label" for="qty-toggle"
                >Unlimited</label
              >
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label for="eventName">Image</label>
            <div class="row">
              <div class="col-9">
                <input
                  type="text"
                  name="featuredImage"
                  v-model="data.image"
                  class="form-control"
                  disabled
                />
              </div>
              <div class="col-3">
                <ActionButton
                  :theme="{ primary, secondary, danger, event }"
                  @customEvent="handleShowMedia"
                  label="Media Gallery"
                  style="width: auto; margin-top: 0; margin-left: auto;"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-12">
          <div class="form-group">
            <label for="description">Description </label>
            <vue-editor
              placeholder="Enter description"
              class="form-control h-auto form-control--rtxt"
              name="description"
              v-model.trim="data.description"
              useCustomImageHandler
              ref="vueEditor"
              @image-added="handleImageAdded"
              @image-removed="handleImageRemoved"
              @text-change="handleTextChange"
              @selection-change="onSelection"
            />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group d-flex">
            <label>
              Capacity:
            </label>
            <div
              class="switch ml-2"
              @click="handleItemStatus('capacity')"
              :class="{ active: !!capacityStatus }"
            >
              <div class="indicator"></div>
            </div>
          </div>
        </div>
        <div class="col-4" v-if="data.capacity > 0">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-prepend">
                <ActionButton
                  mode="outline"
                  style="margin-top:0;"
                  :theme="{ primary, secondary, event, danger }"
                  label="&mdash;"
                  :disabled="!capacityStatus"
                  @customEvent="handleItemCount('capacity', 'sub')"
                />
              </div>
              <input
                type="text"
                class="form-control text-center"
                v-model="data.capacity"
                :disabled="!capacityStatus"
                @keypress="isNumber($event)"
              />
              <div class="input-group-append">
                <ActionButton
                  mode="outline"
                  style="margin-top:0;"
                  :theme="{ primary, secondary, event, danger }"
                  label="+"
                  :disabled="!capacityStatus"
                  @customEvent="handleItemCount('capacity', 'add')"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label for="Inclusions">Inclusions</label>
            <div class="row mt-10">
              <div
                class="col-md-6"
                :key="inclusion.value"
                v-for="inclusion in inclusions"
              >
                <Checkbox
                  :value="data.inclusions.includes(inclusion.value)"
                  :label="inclusion.label"
                  @toggled="handleInclusionToggle(inclusion)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row submit-row">
        <div class="col d-flex">
          <div
            class="switch"
            @click="data.status = !data.status"
            :class="{ active: !!data.status }"
          >
            <div class="indicator"></div>
          </div>
          <span @click="data.status = !data.status">{{
            data.status ? "Active" : "Inactive"
          }}</span>
        </div>
        <div class="col">
          <ActionButton
            type="submit"
            :theme="{ primary, secondary, event, danger }"
            :label="isEdit ? 'Update' : 'Save'"
            style="width: auto; margin-left: auto; margin-top: 0;"
            :disabled="loader"
          />
        </div>
      </div>
    </form>
    <MediaGalleryModal
      v-if="showMediaGallery"
      @response="handleMediaGalleryImage"
      @close="handleCloseMedia"
    />
  </div>
</template>

<script>
import ActionButton from "@/components/ActionButton";
import Spinner from "@/components/Spinner";
import axios from "@/helpers/axios";
import { socketSender } from "@/helpers/socket";
import MediaGalleryModal from "@/components/media-gallery/MediaGalleryModal";
import Checkbox from "@/components/Checkbox";
import { mixin as clickaway } from "vue-clickaway";
import FetchingOverlay from "@/components/FetchingOverlay";
import editor from "@/helpers/editor";
import CustomSelect from "@/components/Select";

export default {
  name: "AddPackage",
  mixins: [clickaway, editor],
  components: {
    ActionButton,
    MediaGalleryModal,
    Spinner,
    Checkbox,
    FetchingOverlay,
    CustomSelect
  },
  props: {
    singleRecord: {
      type: Object,
      default: () => {}
    },
    link: {
      type: String,
      default: null
    }
  },
  data: () => ({
    data: {
      product: {
        label: null,
        value: 0
      },
      terms: [],
      location: null,
      category: null,
      price: [],
      status: false,
      inclusions: [],
      quantity: 1,
      description: null,
      capacity: 0,
      capacityId: 0,
      schedule: null
    },
    isLinked: false,
    products: [],
    terms: [],
    isEdit: false,
    productId: null,
    loader: false,
    showMediaGallery: false,
    inclusions: [],
    showDropdown: false,
    termTable: [],
    quantityStatus: false,
    capacityStatus: false,
    loaders: {
      page: false,
      deleteButton: -1
    },
    proceed: true,
    dataLoaders: {
      locations: true,
      categories: true,
      schedules: false
    },
    isSearching: {
      locations: false,
      categories: false,
      schedules: false
    }
  }),
  created() {
    this.editSelectedPackage();
    this.init();
    this.quantityStatus = this.data.quantity > -1 ? true : false;
    this.capacityStatus = this.data.capacity > 0 ? true : false;
  },
  watch: {
    "data.quantity"() {
      this.quantityStatus =
        this.data.quantity > 0 || this.data.quantity == "" ? true : false;
    },
    "data.capacity"() {
      this.capacityStatus =
        this.data.capacity == "" || this.data.capacity == 0 ? false : true;
    },
    termTable: {
      handler: function(term) {
        if (term.length) {
          this.proceed = true;
          for (const t of term) {
            if (t.price == "") {
              this.proceed = false;
              break;
            }
          }
        }
      },
      deep: true
    }
  },
  methods: {
    init() {
      this.resetList();
      this.paginated("products");
      this.paginated("terms", "status=2");
      this.fetchLocations({ search: "" });
      this.fetchCategories({ search: "" });
      this.fetchSchedules({ search: "" });
      this.paginated("inclusions", "status=2");
    },
    paginated(collection, action = "", page = 1) {
      axios
        .get(`/${collection}?page[number]=${page}&page[size]=10&${action}`)
        .then(response => {
          var { data, meta } = response.data;
          data = data.map(product => ({
            label: product.name,
            value: product.id
          }));
          this[collection] = [...this[collection], ...data];
          if (meta.hasNextPage) {
            this.paginated(collection, action, page + 1);
          } else {
            this.dataLoaders[collection] = false;
          }
        });
    },
    async fetchLocations(params) {
      this.dataLoaders.locations = true;
      let { search } = params;
      let action = search ? `search[name]=${search}` : "";
      await this.$store.dispatch("FETCH_LOCATION_PAGINATE", action);
      this.dataLoaders.locations = false;
      this.isSearching.locations = false;
    },
    async fetchCategories(params) {
      this.dataLoaders.categories = true;
      let { search } = params;
      let action = search ? `search[name]=${search}` : "";
      await this.$store.dispatch("FETCH_CATEGORY_PAGINATE", action);
      this.dataLoaders.categories = false;
      this.isSearching.categories = false;
    },
    async fetchSchedules(params) {
      this.dataLoaders.schedules = true;
      const { search } = params;
      const action =
        search && this.data.schedule && this.data.schedule.label !== search
          ? `search[name]=${search}`
          : "";

      await this.$store.dispatch("FETCH_SCHEDULE_PAGINATE", action);
      this.dataLoaders.schedules = false;
      this.isSearching.schedules = false;
    },
    handleType(collection) {
      switch (collection) {
        case "locations":
          this.$store.dispatch("LOCATION_LIST_RESET");
          this.isSearching.locations = true;
          break;
        case "categories":
          this.$store.dispatch("CATEGORY_LIST_RESET");
          this.isSearching.categories = true;
          break;
        default:
          this.$store.dispatch("SCHEDULE_LIST_RESET");
          this.isSearching.schedules = true;
      }
    },
    resetList() {
      this.$store.dispatch("LOCATION_LIST_RESET");
      this.$store.dispatch("CATEGORY_LIST_RESET");
      this.$store.dispatch("SCHEDULE_LIST_RESET");
    },
    async submitForm(event) {
      try {
        this.loader = true;
        event.preventDefault();
        var productForm = {};
        var metaDataForm = {};
        var result = {};
        const {
          product,
          terms,
          status,
          price,
          image,
          description,
          quantity,
          category,
          location,
          inclusions,
          capacity,
          capacityId,
          schedule
        } = this.data;

        if (this.validate(this.data)) {
          productForm.name = product.label;
          productForm.image = image;
          productForm.description = description;
          productForm.quantity =
            this.quantityStatus && parseInt(quantity) ? quantity : -1;
          productForm.categoryId = category.value;
          productForm.locationId = location.value;
          productForm.inclusions = inclusions;
          productForm.terms = terms;
          productForm.prices = price;
          productForm.status = status ? 2 : 1;
          productForm.scheduleId = schedule.value;

          if (this.productId) {
            result = await axios
              .patch(`/products/${this.productId}`, productForm)
              .then(async result => {
                const { data } = result;
                return data;
              });
          } else {
            result = await axios
              .post(`/products`, productForm)
              .then(async result => {
                const { data } = result;
                return data;
              });
            await socketSender(
              "updateLog",
              `${this.user} added a new Product: ${productForm.name}`
            );
          }

          var packageForm = {};
          this.termTable.forEach(async term => {
            packageForm = {
              productId: this.productId ? this.productId : result.data.id,
              termId: term.value,
              price: term.price,
              status: 1
            };
            if (term.id) {
              await axios.patch(`/packages/${term.id}`, packageForm);
            } else {
              await axios.post(`/packages`, packageForm);
            }
          });

          metaDataForm.key = "product.capacity";
          metaDataForm.value = capacity;
          metaDataForm.label = "Capacity";
          metaDataForm.referenceId = this.productId
            ? this.productId
            : result.data.id;

          if (capacityId) {
            await axios.patch(`/metadatas/${capacityId}`, metaDataForm);
          } else {
            await axios.post("/metadatas", metaDataForm);
          }

          if (result) {
            this.$toastr.success("Your changes are saved", "Success");
            await this.$store.dispatch("FETCH_COLLECTION_PER_PAGE", this.link);
            this.$emit("close");
          }

          if (this.$router.currentRoute.name !== "products") {
            this.$router.push("/admin/products");
          }
        } else {
          this.$toastr.warning("Please complete the form", "Warning");
        }
      } catch (error) {
        const errType = error.type || "error";
        const errTitle = error.title || "Error";
        const errMsg = error.error.message || error.message;

        this.$toastr[errType](errMsg, errTitle);
      } finally {
        this.loader = false;
      }
    },
    editSelectedPackage() {
      if (this.singleRecord) {
        const item = this.singleRecord;
        var data = {
          terms: item && item.Terms ? item.Terms.map(term => term.id) : [],
          product: {
            label: item.name,
            value: item.id
          },
          location: {
            label: item.Location.name,
            value: item.Location.id
          },
          category: {
            label: item.Category.name,
            value: item.Category.id
          },
          price:
            item && item.Packages ? item.Packages.map(pack => pack.price) : [],
          quantity: item.quantity,
          status: item.status > 1 ? true : false,
          description: item.description,
          inclusions:
            item && item.Inclusions
              ? item.Inclusions.map(inclusion => inclusion.id)
              : [],
          capacity: item.MetaDatum ? item.MetaDatum.value : 0,
          capacityId: item.MetaDatum ? item.MetaDatum.id : 0,
          schedule: {
            label: item.Schedule ? item.Schedule.name : "Open 24/7",
            value: item.Schedule ? item.Schedule.id : 1
          }
        };
        this.isLinked = !!(item.ProductLink && item.ProductLink.id);

        this.data = data;
        var toArray = [];
        if (item.Terms) {
          item.Terms.forEach(data => {
            toArray.push({
              value: data.id,
              label: data.name,
              price: data.Package.price,
              id: data.Package.id
            });
          });
        }
        this.termTable = toArray;
        this.isEdit = true;
        this.productId = item.id;
      }
    },
    handleMediaGalleryImage(obj) {
      this.data.image = obj.original;
    },
    handleInclusionToggle(inclusion) {
      var { inclusions } = this.data;
      if (inclusions.includes(inclusion.value)) {
        inclusions = inclusions.filter(
          inInclusions => inInclusions != inclusion.value
        );
      } else {
        inclusions.push(inclusion.value);
      }
      this.data.inclusions = inclusions;
    },
    handleTermToggle(term) {
      var { terms } = this.data;
      const termTable = this.termTable.find(t => t.value === term.value);
      if (termTable) {
        this.handleDeleteTerm(termTable);
        return false;
      } else {
        if (terms.includes(term.value)) {
          terms = terms.filter(inTerms => inTerms != term.value);
          this.data.terms = terms;
        } else {
          terms.push(term.value);
        }
      }
    },
    handleApplyTerm() {
      let arr = [];
      for (const id of this.data.terms) {
        const input = this.termTable.find(t => t.value == id);
        if (input) {
          arr.push(input);
        } else {
          const term = this.terms.find(t => t.value == id);
          arr.push({
            value: term.value,
            label: term.label,
            price: 1,
            id: 0
          });
        }
      }
      this.termTable = arr;
      this.showDropdown = false;
    },
    handleHideDropdown() {
      this.showDropdown = false;
    },
    handleItemStatus(item) {
      switch (item) {
        case "quantity":
          this.quantityStatus = !this.quantityStatus;
          this.data.quantity = this.quantityStatus ? 1 : -1;
          break;
        case "capacity":
          this.capacityStatus = !this.capacityStatus;
          this.data.capacity = this.capacityStatus ? 1 : 0;
          break;
      }
    },
    handleItemCount(item, ops) {
      switch (item) {
        case "quantity":
          this.data.quantity =
            ops == "add"
              ? parseInt(this.data.quantity) + 1
              : Math.max(parseInt(this.data.quantity) - 1, 0);
          if (this.data.quantity === 0) {
            this.data.quantity = 1;
          }
          this.quantityStatus = this.data.quantity > -1 ? true : false;
          break;
        case "capacity":
          this.data.capacity =
            ops == "add"
              ? parseInt(this.data.capacity) + 1
              : parseInt(this.data.capacity) - 1;
          this.capacityStatus = this.data.capacity > 0 ? true : false;
          break;
      }
    },
    async handleDeleteTerm(term) {
      const { value, id, label } = term;
      if (id) {
        this.loaders.deleteButton = value;
        const orders = await axios
          .get(`/orders?OrderPackage[packageId]=${id}`)
          .then(result => {
            const { data } = result.data;
            return data;
          });

        this.loaders.deleteButton = -1;
        if (orders.length === 0) {
          this.$swal({
            title: `Are you sure you want to delete "${term.label}"?`,
            type: "warning",
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            showCancelButton: true,
            reverseButtons: true
          }).then(async result => {
            if (result.value) {
              await axios.delete(`/packages/${id}`);
              socketSender(
                "updateLog",
                `${this.user} removed the package named ${label}`
              );
              this.termTable = this.termTable.filter(
                term => term.value != value
              );
              this.data.terms = this.data.terms.filter(term => term != value);
              delete this.data.price[value];
              await this.$store.dispatch(
                "FETCH_COLLECTION_PER_PAGE",
                this.link
              );
            }
          });
        } else {
          this.$swal({
            title:
              "Can't delete this package entry since it is associated with active records.",
            type: "error"
          });
        }
      } else {
        this.termTable = this.termTable.filter(term => term.value != value);
        this.data.terms = this.data.terms.filter(term => term != value);
        delete this.data.price[value];
      }
    },
    validate(data) {
      const { product, location, category, quantity, schedule } = data;
      const validate =
        product.label &&
        product.label != "" &&
        location &&
        category &&
        quantity != "" &&
        schedule &&
        this.proceed;

      if (validate) {
        if (product.label.length > 100) {
          throw {
            type: "error",
            title: "Warning",
            error: new Error(
              "You have reached the maximum limit of characters allowed"
            )
          };
        }
        return true;
      } else {
        if (!product.label) {
          this.data.product.label = "";
        }
        if (!location) {
          this.data.location = "";
        }
        if (!category) {
          this.data.category = "";
        }
        if (!schedule) {
          this.data.schedule = "";
        }
        return false;
      }
    },
    isNumber: function(evt) {
      let keyCode = evt.keyCode ? evt.keyCode : evt.which;
      if (keyCode == 48 && evt.target.value.length == 0) {
        evt.preventDefault();
      }

      if (
        (keyCode < 48 || keyCode > 57) &&
        (keyCode !== 46 || evt.target.value.indexOf(".") != -1)
      ) {
        evt.preventDefault();
      }
      if (
        evt.target.value != null &&
        evt.target.value.indexOf(".") > -1 &&
        evt.target.value.split(".")[1].length > 1
      ) {
        evt.preventDefault();
      }
    },
    handleShowMedia() {
      this.showMediaGallery = true;
      this.$emit("toggleScroll", { scroll: false });
    },
    handleCloseMedia() {
      this.showMediaGallery = false;
      this.$emit("toggleScroll", { scroll: true });
    }
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    user() {
      return this.$store.getters.getFullUser;
    },
    handleListClass() {
      return "term-dropdown " + (this.showDropdown ? "show" : "");
    },
    locations() {
      return this.$store.state.admin.locations;
    },
    categories() {
      return this.$store.state.admin.categories;
    },
    schedules() {
      return this.$store.state.admin.schedules;
    }
  }
};
</script>

<style lang="scss" scoped>
.product-template {
  .term-dropdown {
    list-style-type: none;
    margin: 0;
    position: absolute;
    background-color: #fff;
    height: auto;
    z-index: 3;
    margin-top: 10px;
    box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.3);
    border-radius: 5px;
    min-width: 300px;
    flex-direction: column;
    padding: 15px 0px;
    opacity: 0;
    visibility: hidden;
    &.show {
      opacity: 1;
      visibility: visible;
      transition: 0.3s;
    }
  }
  .product-term-holder {
    .uni-row {
      margin-left: -15px;
      margin-right: -15px;
    }
    .custom-is-invalid {
      border: 1px solid #ff0000;
    }
    button.btn {
      border: none;
      height: 23px !important;
      padding: 0 !important;
      transition: all 0.3s ease;
      width: 23px !important;
      &:hover {
        background-color: $gray-250;
      }
      &.btn {
        &-danger {
          background-color: #ef5350 !important;
          &:hover {
            background-color: #c82333 !important;
          }
        }
      }
    }
  }
  .quantity {
    &--wrapper {
      border: 1px solid rgb(204, 204, 204);
      border-radius: 5px;
    }
    &--btn {
      .btn {
        font-size: 20px;
      }
    }
    &--field {
      border: none !important;
    }
  }
}

.tooltipText {
  z-index: 10;
  opacity: 0;
  content: "";
  visibility: hidden;
  transition: 300ms;
  position: absolute;
  background: #4c4c4c;
  width: 180px;
  height: auto;
  border-radius: 5px;
  top: 73px;
  left: 0;
  padding: 4px 12px;
  p {
    white-space: normal;
    text-align: start;
    font-size: 12px;
    margin-bottom: 0;
    color: #fff;
  }
}

.disabled--field {
  &:after {
    opacity: 0;
    content: "";
    visibility: hidden;
    transition: 300ms;
    position: absolute;
    border-style: solid;
    border-color: transparent transparent #4c4c4c transparent;
    border-width: 8px;
    top: 58px;
    left: 80px;
  }

  &:hover {
    .tooltipText,
    &:after {
      opacity: 1;
      visibility: visible;
    }
  }
}

.form-control--rtxt {
  border-color: transparent;
  padding: 0;
}

.schedule-dropdown {
  /deep/.dropdown-menu {
    width: 100%;
    max-height: 187px !important;

    li {
      a {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
